import { useState, useEffect } from "react";
import { useAuth, useLanguage, API } from "../App";
import { AppLayout } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Image, Upload, FileText, Download, History, Plus, Clock, Layers,
  MoreVertical, Pencil, Trash2, Archive, MessageSquare, CheckCircle2, Loader2, Send, Eye, X, ZoomIn, ZoomOut, Maximize2
} from "lucide-react";
import { toast } from "sonner";

export const DrawingsPage = () => {
  const { user, token } = useAuth();
  const { language } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [drawings, setDrawings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewType, setPreviewType] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewZoom, setPreviewZoom] = useState(100);
  const [annotationDialogOpen, setAnnotationDialogOpen] = useState(false);
  const [selectedDrawing, setSelectedDrawing] = useState(null);
  const [versions, setVersions] = useState([]);
  const [annotations, setAnnotations] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [versionFile, setVersionFile] = useState(null);
  const [versionNotes, setVersionNotes] = useState("");
  const [formData, setFormData] = useState({ name: "", description: "" });
  const [editForm, setEditForm] = useState({ name: "", description: "" });
  const [annotationText, setAnnotationText] = useState("");
  const [annotationType, setAnnotationType] = useState("comment");
  const [submitting, setSubmitting] = useState(false);

  const t = (sv, en) => language === "sv" ? sv : en;
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchProjects(); }, []);
  useEffect(() => { if (selectedProject) fetchDrawings(selectedProject); }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API}/projects`, { headers, credentials: "include" });
      if (res.ok) { const data = await res.json(); setProjects(data); if (data.length > 0) setSelectedProject(data[0].project_id); }
    } catch (err) { console.error("Failed:", err); }
    finally { setLoading(false); }
  };

  const fetchDrawings = async (projectId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/drawings`, { headers, credentials: "include" });
      if (res.ok) setDrawings(await res.json());
    } catch (err) { console.error("Failed:", err); }
    finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !selectedProject) return;
    setSubmitting(true);
    const fd = new FormData();
    fd.append("file", uploadFile);
    fd.append("name", formData.name);
    fd.append("description", formData.description);
    try {
      const res = await fetch(`${API}/projects/${selectedProject}/drawings`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, credentials: "include", body: fd
      });
      if (res.ok) {
        toast.success(t("Ritning uppladdad!", "Drawing uploaded!"));
        setDialogOpen(false); setUploadFile(null); setFormData({ name: "", description: "" });
        fetchDrawings(selectedProject);
      } else { toast.error(t("Uppladdning misslyckades", "Upload failed")); }
    } catch { toast.error(t("Ett fel uppstod", "An error occurred")); }
    finally { setSubmitting(false); }
  };

  const handleNewVersion = async (e) => {
    e.preventDefault();
    if (!versionFile || !selectedDrawing) return;
    setSubmitting(true);
    const fd = new FormData();
    fd.append("file", versionFile);
    fd.append("notes", versionNotes);
    try {
      const res = await fetch(`${API}/drawings/${selectedDrawing.drawing_id}/versions`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, credentials: "include", body: fd
      });
      if (res.ok) {
        toast.success(t("Ny version uppladdad!", "New version uploaded!"));
        setVersionDialogOpen(false); setVersionFile(null); setVersionNotes("");
        fetchDrawings(selectedProject);
      } else { toast.error(t("Uppladdning misslyckades", "Upload failed")); }
    } catch { toast.error(t("Ett fel uppstod", "An error occurred")); }
    finally { setSubmitting(false); }
  };

  const fetchVersions = async (drawing) => {
    setSelectedDrawing(drawing);
    try {
      const res = await fetch(`${API}/drawings/${drawing.drawing_id}/versions`, { headers, credentials: "include" });
      if (res.ok) { setVersions(await res.json()); setHistoryDialogOpen(true); }
    } catch { toast.error(t("Kunde inte hamta historik", "Could not fetch history")); }
  };

  const downloadCurrent = (drawing) => {
    window.open(`${API}/drawings/${drawing.drawing_id}/download`, '_blank');
  };

  const downloadVersion = (versionId) => {
    window.open(`${API}/drawings/versions/${versionId}/download`, '_blank');
  };

  const canPreview = (fileType) => {
    const ft = (fileType || '').toLowerCase();
    return ft.includes('pdf') || ft.includes('png') || ft.includes('jpg') || ft.includes('jpeg') || ft.includes('gif');
  };

  const openPreview = async (drawing) => {
    const ft = (drawing.file_type || '').toLowerCase();
    if (!canPreview(ft)) {
      toast.info(t("Förhandsgranskning stöds inte för denna filtyp", "Preview not supported for this file type"));
      return;
    }
    try {
      const res = await fetch(`${API}/drawings/${drawing.drawing_id}/preview`, { headers, credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewType(ft.includes('pdf') ? 'pdf' : 'image');
      setPreviewTitle(`${drawing.name} — v${drawing.current_version}`);
      setPreviewZoom(100);
      setPreviewOpen(true);
    } catch {
      toast.error(t("Kunde inte ladda förhandsgranskning", "Could not load preview"));
    }
  };

  const openVersionPreview = async (versionId, versionNum, drawingName, fileType) => {
    const ft = (fileType || '').toLowerCase();
    if (!canPreview(ft)) {
      toast.info(t("Förhandsgranskning stöds inte för denna filtyp", "Preview not supported for this file type"));
      return;
    }
    try {
      const res = await fetch(`${API}/drawings/versions/${versionId}/preview`, { headers, credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewType(ft.includes('pdf') ? 'pdf' : 'image');
      setPreviewTitle(`${drawingName} — v${versionNum}`);
      setPreviewZoom(100);
      setPreviewOpen(true);
    } catch {
      toast.error(t("Kunde inte ladda förhandsgranskning", "Could not load preview"));
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewOpen(false);
    setPreviewUrl("");
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedDrawing) return;
    try {
      const params = new URLSearchParams();
      if (editForm.name) params.set("name", editForm.name);
      if (editForm.description !== undefined) params.set("description", editForm.description);
      const res = await fetch(`${API}/drawings/${selectedDrawing.drawing_id}?${params}`, {
        method: "PUT", headers, credentials: "include"
      });
      if (res.ok) {
        toast.success(t("Ritning uppdaterad!", "Drawing updated!"));
        setEditDialogOpen(false);
        fetchDrawings(selectedProject);
      }
    } catch { toast.error(t("Kunde inte uppdatera", "Could not update")); }
  };

  const handleDelete = async (drawing) => {
    if (!window.confirm(t("Vill du verkligen radera denna ritning och alla versioner?", "Are you sure you want to delete this drawing and all versions?"))) return;
    try {
      const res = await fetch(`${API}/drawings/${drawing.drawing_id}`, {
        method: "DELETE", headers, credentials: "include"
      });
      if (res.ok) {
        toast.success(t("Ritning raderad", "Drawing deleted"));
        fetchDrawings(selectedProject);
      }
    } catch { toast.error(t("Kunde inte radera", "Could not delete")); }
  };

  const handleArchive = async (drawing) => {
    const newStatus = drawing.status === "archived" ? "active" : "archived";
    try {
      const res = await fetch(`${API}/drawings/${drawing.drawing_id}?status=${newStatus}`, {
        method: "PUT", headers, credentials: "include"
      });
      if (res.ok) {
        toast.success(newStatus === "archived" ? t("Ritning arkiverad", "Drawing archived") : t("Ritning aktiverad", "Drawing activated"));
        fetchDrawings(selectedProject);
      }
    } catch { toast.error(t("Kunde inte ändra status", "Could not change status")); }
  };

  // Annotations
  const fetchAnnotations = async (drawing) => {
    setSelectedDrawing(drawing);
    try {
      const res = await fetch(`${API}/drawings/${drawing.drawing_id}/annotations`, { headers, credentials: "include" });
      if (res.ok) { setAnnotations(await res.json()); setAnnotationDialogOpen(true); }
    } catch { toast.error(t("Kunde inte hamta anteckningar", "Could not fetch annotations")); }
  };

  const addAnnotation = async () => {
    if (!annotationText.trim() || !selectedDrawing) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/drawings/${selectedDrawing.drawing_id}/annotations`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text: annotationText, type: annotationType, version_number: selectedDrawing.current_version })
      });
      if (res.ok) {
        toast.success(t("Anteckning tillagd!", "Annotation added!"));
        setAnnotationText("");
        // Refresh
        const res2 = await fetch(`${API}/drawings/${selectedDrawing.drawing_id}/annotations`, { headers, credentials: "include" });
        if (res2.ok) setAnnotations(await res2.json());
      }
    } catch { toast.error(t("Kunde inte lägga till", "Could not add")); }
    finally { setSubmitting(false); }
  };

  const resolveAnnotation = async (annotationId) => {
    try {
      await fetch(`${API}/drawings/annotations/${annotationId}/resolve`, {
        method: "PUT", headers, credentials: "include"
      });
      const res = await fetch(`${API}/drawings/${selectedDrawing.drawing_id}/annotations`, { headers, credentials: "include" });
      if (res.ok) setAnnotations(await res.json());
      toast.success(t("Markerad som löst", "Marked as resolved"));
    } catch { toast.error(t("Kunde inte lösa", "Could not resolve")); }
  };

  const deleteAnnotation = async (annotationId) => {
    try {
      await fetch(`${API}/drawings/annotations/${annotationId}`, {
        method: "DELETE", headers, credentials: "include"
      });
      setAnnotations(prev => prev.filter(a => a.annotation_id !== annotationId));
      toast.success(t("Anteckning raderad", "Annotation deleted"));
    } catch { toast.error(t("Kunde inte radera", "Could not delete")); }
  };

  const getFileTypeIcon = (fileType) => {
    const ft = (fileType || '').toLowerCase();
    if (ft.includes('pdf')) return 'PDF';
    if (ft.includes('dwg')) return 'DWG';
    if (ft.includes('png') || ft.includes('jpg') || ft.includes('jpeg')) return 'IMG';
    return 'FIL';
  };

  const getFileTypeColor = (fileType) => {
    const ft = (fileType || '').toLowerCase();
    if (ft.includes('pdf')) return 'bg-red-100 text-red-700';
    if (ft.includes('dwg')) return 'bg-blue-100 text-blue-700';
    if (ft.includes('png') || ft.includes('jpg')) return 'bg-green-100 text-green-700';
    return 'bg-slate-100 text-slate-700';
  };

  const typeLabels = { comment: t("Kommentar", "Comment"), issue: t("Problem", "Issue"), revision: t("Revision", "Revision") };
  const typeColors = { comment: "bg-blue-100 text-blue-700", issue: "bg-red-100 text-red-700", revision: "bg-amber-100 text-amber-700" };

  return (
    <AppLayout>
      <div className="p-4 lg:p-6 space-y-6 pb-20 lg:pb-6" data-testid="drawings-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
              {t("Ritningar", "Drawings")}
            </h1>
            <p className="text-slate-600 mt-1">{t("Hantera projektritningar, versioner och anteckningar", "Manage project drawings, versions and annotations")}</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-touch" disabled={!selectedProject} data-testid="upload-drawing-btn">
                <Upload className="w-5 h-5 mr-2" />{t("Ladda upp", "Upload")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>{t("Ladda upp ritning", "Upload Drawing")}</DialogTitle></DialogHeader>
              <form onSubmit={handleUpload} className="space-y-4">
                <div className="space-y-2">
                  <Label>{t("Ritningsnamn", "Drawing name")} *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder={t("T.ex. A-101 Planritning", "E.g. A-101 Floor plan")} required data-testid="drawing-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>{t("Beskrivning", "Description")}</Label>
                  <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder={t("Valfri beskrivning...", "Optional description...")} />
                </div>
                <div className="space-y-2">
                  <Label>{t("Fil", "File")} *</Label>
                  <Input type="file" accept=".pdf,.dwg,.png,.jpg,.jpeg" onChange={(e) => setUploadFile(e.target.files[0])}
                    className="h-auto py-2" required data-testid="drawing-file-input" />
                  <p className="text-xs text-slate-500">{t("Stöder PDF, DWG, PNG, JPG", "Supports PDF, DWG, PNG, JPG")}</p>
                </div>
                <Button type="submit" className="w-full" disabled={!uploadFile || submitting} data-testid="drawing-submit-btn">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {t("Ladda upp", "Upload")}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-full sm:w-[300px] h-10" data-testid="drawing-project-select">
              <SelectValue placeholder={t("Välj projekt", "Select project")} />
            </SelectTrigger>
            <SelectContent>
              {projects.map(p => (<SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <div className="text-sm text-slate-500 flex items-center gap-1">
            <Layers className="w-4 h-4" /> {drawings.length} {t("ritningar", "drawings")}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : !selectedProject ? (
          <Card><CardContent className="py-12 text-center"><Image className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-600">{t("Välj ett projekt för att se ritningar", "Select a project to view drawings")}</p></CardContent></Card>
        ) : drawings.length === 0 ? (
          <Card><CardContent className="py-12 text-center"><FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-600">{t("Inga ritningar ännu", "No drawings yet")}</p><p className="text-sm text-slate-500 mt-2">{t("Ladda upp din första ritning", "Upload your first drawing")}</p></CardContent></Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {drawings.map((drawing) => (
              <Card key={drawing.drawing_id} className={`hover:shadow-md transition-shadow ${drawing.status === 'archived' ? 'opacity-60' : ''}`} data-testid={`drawing-card-${drawing.drawing_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4 cursor-pointer" onClick={() => openPreview(drawing)}>
                    <div className={`p-3 rounded-lg flex-shrink-0 ${getFileTypeColor(drawing.file_type)} relative group`}>
                      <span className="text-sm font-bold">{getFileTypeIcon(drawing.file_type)}</span>
                      {canPreview(drawing.file_type) && (
                        <div className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Eye className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <h3 className="font-semibold text-slate-900 truncate" style={{ fontFamily: 'Barlow Condensed' }}>{drawing.name}</h3>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7 -mt-1" data-testid={`drawing-menu-${drawing.drawing_id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canPreview(drawing.file_type) && (
                              <DropdownMenuItem onClick={() => openPreview(drawing)} data-testid={`drawing-preview-${drawing.drawing_id}`}>
                                <Eye className="w-4 h-4 mr-2" />{t("Förhandsgranska", "Preview")}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => downloadCurrent(drawing)}>
                              <Download className="w-4 h-4 mr-2" />{t("Ladda ner", "Download")}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setSelectedDrawing(drawing); setEditForm({ name: drawing.name, description: drawing.description || "" }); setEditDialogOpen(true); }}>
                              <Pencil className="w-4 h-4 mr-2" />{t("Redigera", "Edit")}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleArchive(drawing)}>
                              <Archive className="w-4 h-4 mr-2" />{drawing.status === "archived" ? t("Aktivera", "Activate") : t("Arkivera", "Archive")}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDelete(drawing)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />{t("Radera", "Delete")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      {drawing.description && (<p className="text-sm text-slate-600 truncate mt-1">{drawing.description}</p>)}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge variant="outline" className="text-xs">v{drawing.current_version}</Badge>
                        <span className="text-xs text-slate-500 uppercase">{(drawing.file_type || '').replace('.', '')}</span>
                        <Badge className={`text-xs ${drawing.status === 'active' ? 'bg-green-100 text-green-700' : drawing.status === 'archived' ? 'bg-slate-100 text-slate-600' : 'bg-amber-100 text-amber-700'}`}>
                          {drawing.status === 'active' ? t('Aktiv', 'Active') : drawing.status === 'archived' ? t('Arkiverad', 'Archived') : drawing.status}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mt-4">
                    <Button variant="outline" size="sm" onClick={() => fetchVersions(drawing)} data-testid={`drawing-history-${drawing.drawing_id}`}>
                      <History className="w-4 h-4 mr-1" />{t("Historik", "History")}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => fetchAnnotations(drawing)} data-testid={`drawing-annotations-${drawing.drawing_id}`}>
                      <MessageSquare className="w-4 h-4 mr-1" />{t("Noteringar", "Notes")}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setSelectedDrawing(drawing); setVersionDialogOpen(true); }} data-testid={`drawing-new-version-${drawing.drawing_id}`}>
                      <Plus className="w-4 h-4 mr-1" />{t("Version", "Version")}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Version History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {t("Versionshistorik", "Version History")} — {selectedDrawing?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {versions.length === 0 ? (
              <p className="text-slate-500 text-center py-6">{t("Ingen historik", "No history")}</p>
            ) : (
              versions.map((v) => (
                <div key={v.version_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200" data-testid={`version-${v.version_id}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">v{v.version_number}</Badge>
                      {v.version_number === selectedDrawing?.current_version && (
                        <Badge className="bg-green-100 text-green-700 text-xs">{t("Aktuell", "Current")}</Badge>
                      )}
                    </div>
                    {v.notes && <p className="text-sm text-slate-600 mt-1">{v.notes}</p>}
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(v.uploaded_at).toLocaleString('sv-SE')}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {canPreview(selectedDrawing?.file_type) && (
                      <Button variant="ghost" size="sm" onClick={() => openVersionPreview(v.version_id, v.version_number, selectedDrawing?.name, selectedDrawing?.file_type)} data-testid={`preview-version-${v.version_id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => downloadVersion(v.version_id)} data-testid={`download-version-${v.version_id}`}>
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Annotations Dialog */}
      <Dialog open={annotationDialogOpen} onOpenChange={setAnnotationDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {t("Anteckningar", "Annotations")} — {selectedDrawing?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Add annotation form */}
            <div className="flex gap-2">
              <div className="flex-1 space-y-2">
                <Textarea
                  value={annotationText}
                  onChange={(e) => setAnnotationText(e.target.value)}
                  placeholder={t("Skriv en anteckning...", "Write an annotation...")}
                  className="min-h-[60px]"
                  data-testid="annotation-text-input"
                />
                <div className="flex items-center gap-2">
                  <Select value={annotationType} onValueChange={setAnnotationType}>
                    <SelectTrigger className="w-[140px] h-8" data-testid="annotation-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="comment">{t("Kommentar", "Comment")}</SelectItem>
                      <SelectItem value="issue">{t("Problem", "Issue")}</SelectItem>
                      <SelectItem value="revision">{t("Revision", "Revision")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={addAnnotation} disabled={!annotationText.trim() || submitting} data-testid="add-annotation-btn">
                    {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                    {t("Lägg till", "Add")}
                  </Button>
                </div>
              </div>
            </div>

            {/* Annotations list */}
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {annotations.length === 0 ? (
                <p className="text-slate-500 text-center py-6 text-sm">{t("Inga anteckningar ännu", "No annotations yet")}</p>
              ) : (
                annotations.map((a) => (
                  <div key={a.annotation_id} className={`p-3 rounded-lg border ${a.resolved ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'}`} data-testid={`annotation-${a.annotation_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`text-[10px] ${typeColors[a.type] || 'bg-slate-100 text-slate-700'}`}>
                            {typeLabels[a.type] || a.type}
                          </Badge>
                          <span className="text-xs text-slate-400">v{a.version_number}</span>
                          {a.resolved && <Badge className="bg-green-100 text-green-700 text-[10px]">{t("Löst", "Resolved")}</Badge>}
                        </div>
                        <p className="text-sm text-slate-800">{a.text}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {a.created_by_name} — {new Date(a.created_at).toLocaleString('sv-SE')}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {!a.resolved && (
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => resolveAnnotation(a.annotation_id)} title={t("Markera som löst", "Mark as resolved")}>
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteAnnotation(a.annotation_id)} title={t("Radera", "Delete")}>
                          <Trash2 className="w-4 h-4 text-slate-400" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Drawing Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>{t("Redigera ritning", "Edit drawing")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2">
              <Label>{t("Ritningsnamn", "Drawing name")}</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} data-testid="edit-drawing-name" />
            </div>
            <div className="space-y-2">
              <Label>{t("Beskrivning", "Description")}</Label>
              <Textarea value={editForm.description} onChange={(e) => setEditForm({...editForm, description: e.target.value})} data-testid="edit-drawing-description" />
            </div>
            <Button type="submit" className="w-full" data-testid="edit-drawing-submit">
              {t("Spara", "Save")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* New Version Upload Dialog */}
      <Dialog open={versionDialogOpen} onOpenChange={setVersionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {t("Ladda upp ny version", "Upload new version")} — {selectedDrawing?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleNewVersion} className="space-y-4">
            <div className="text-sm text-slate-600">
              {t("Nuvarande version:", "Current version:")} <Badge variant="outline">v{selectedDrawing?.current_version}</Badge> → <Badge className="bg-blue-100 text-blue-700">v{(selectedDrawing?.current_version || 0) + 1}</Badge>
            </div>
            <div className="space-y-2">
              <Label>{t("Fil", "File")} *</Label>
              <Input type="file" accept=".pdf,.dwg,.png,.jpg,.jpeg" onChange={(e) => setVersionFile(e.target.files[0])}
                className="h-auto py-2" required data-testid="version-file-input" />
            </div>
            <div className="space-y-2">
              <Label>{t("Ändringsnotering", "Change notes")}</Label>
              <Input value={versionNotes} onChange={(e) => setVersionNotes(e.target.value)}
                placeholder={t("Vad har ändrats...", "What changed...")} data-testid="version-notes-input" />
            </div>
            <Button type="submit" className="w-full" disabled={!versionFile || submitting} data-testid="version-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {t("Ladda upp version", "Upload version")} v{(selectedDrawing?.current_version || 0) + 1}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Overlay */}
      {previewOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex flex-col" data-testid="drawing-preview-overlay">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-slate-900/90 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5 text-white/70" />
              <span className="text-white font-medium text-sm">{previewTitle}</span>
            </div>
            <div className="flex items-center gap-2">
              {previewType === 'image' && (
                <>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-white/10" onClick={() => setPreviewZoom(z => Math.max(25, z - 25))} data-testid="preview-zoom-out">
                    <ZoomOut className="w-4 h-4" />
                  </Button>
                  <span className="text-white/70 text-xs w-12 text-center">{previewZoom}%</span>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-white/10" onClick={() => setPreviewZoom(z => Math.min(300, z + 25))} data-testid="preview-zoom-in">
                    <ZoomIn className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-white/10" onClick={() => setPreviewZoom(100)}>
                    <Maximize2 className="w-4 h-4" />
                  </Button>
                </>
              )}
              <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-white/10 ml-2" onClick={closePreview} data-testid="preview-close-btn">
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
          {/* Content */}
          <div className="flex-1 overflow-auto flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) closePreview(); }}>
            {previewType === 'pdf' ? (
              <iframe
                src={`${previewUrl}#toolbar=1&navpanes=0`}
                className="w-full h-full rounded-lg bg-white"
                style={{ maxWidth: '1200px', minHeight: '80vh' }}
                title="PDF Preview"
                data-testid="preview-pdf-iframe"
              />
            ) : (
              <img
                src={previewUrl}
                alt={previewTitle}
                className="rounded-lg shadow-2xl transition-transform duration-200"
                style={{ transform: `scale(${previewZoom / 100})`, maxWidth: 'none' }}
                data-testid="preview-image"
              />
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
};
